/**
 * Compliance report page for a site.
 *
 * Shows:
 * - Overall compliance posture (latest scan stats)
 * - Issue trend chart (ASCII sparkline + data table)
 * - WCAG coverage breakdown
 *
 * API endpoints used:
 *   GET /reports/sites/:id/compliance  — overall compliance posture
 *   GET /reports/sites/:id/trend       — scan history with issue trend
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import { api, ApiError, fetchAuthenticated } from '../lib/api-client';
import { useAuth } from '../hooks/useAuth';
import LockedFeatureState from '../components/entitlements/LockedFeatureState';
import ScanReviewPanel from '../components/review/ScanReviewPanel';
import Container from '../components/ui/Container';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge, { BadgeVariant } from '../components/ui/Badge';
import PageHeader from '../components/ui/PageHeader';
import SectionHeader from '../components/ui/SectionHeader';
import AlertBanner from '../components/ui/AlertBanner';
import StatCard from '../components/ui/StatCard';
import BrandLogo from '../components/brand/BrandLogo';
import { buildBillingUpgradePath, getPlanLabel } from '../lib/plans';
import { deriveScanReview } from '../lib/scan-review';
import { formatDate, formatDateTime } from '../i18n/formatters';

// -------------------------------------------------------------------
// Types
// -------------------------------------------------------------------

interface ComplianceReport {
  site: {
    id: string;
    name: string;
    baseUrl: string;
  };
  totalScans: number;
  latestScan: {
    id: string;
    completedAt: string | null;
    pagesScanned: number;
    totalIssues: number;
    criticalIssues: number;
  } | null;
  latestAuthenticatedScan: {
    scanId: string;
    status: string;
    mode: string;
    failureCategory: string | null;
    message: string | null;
    authenticatedPagesScanned: number;
    discoveredRouteCount: number;
    completedAt: string | null;
    failedAt: string | null;
  } | null;
  latestRegression: {
    scanId: string;
    state: 'baseline' | 'improved' | 'stable' | 'regressed';
    message: string | null;
    scoreDelta: number;
    issuesDelta: number;
    criticalDelta: number;
    reappearedIssueCount: number;
  } | null;
  riskSnapshot: {
    state: 'stable' | 'attention' | 'critical';
    summary: string;
  };
  openIssues: { critical: number; serious: number; moderate: number; minor: number };
  workflowSummary: {
    open: number;
    acknowledged: number;
    inProgress: number;
    resolved: number;
    falsePositive: number;
    suppressed: number;
  };
  suppressedSummary: {
    total: number;
  };
  wcagCoverage: string; // 'A' | 'AA' | 'AAA' | 'none'
  trend: TrendEntry[];
  latestTopIssues: Array<{
    issueId: string;
    code: string;
    title: string;
    impact: string;
    wcagCriteria: string;
    occurrenceCount: number;
    samplePageUrl: string | null;
    samplePageEvidenceId: string | null;
    likelyCause: string;
    developerFixGuidance: string;
    workflowState: string;
    suppressedCount: number;
    deepLinks: {
      scanDetailPath: string;
      siteCompliancePath: string;
      evidencePath: string | null;
    };
  }>;
}

interface TrendEntry {
  scanId: string;
  completedAt: string | null;
  totalIssues: number;
  criticalIssues: number;
  pagesScanned: number;
}

interface OrgUsageResponse {
  plan: string;
  limits: {
    regressionHistoryDepth: number;
    complianceExportEnabled: boolean;
  };
}

// -------------------------------------------------------------------
// Sparkline component (pure CSS/SVG, no chart library)
// -------------------------------------------------------------------

function TrendSparkline({ data, valueKey }: { data: TrendEntry[]; valueKey: 'totalIssues' | 'criticalIssues' }) {
  if (data.length < 2) return null;

  const values = data.map((d) => d[valueKey]);
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const width = 300;
  const height = 60;
  const padding = 4;

  const points = values.map((v, i) => {
    const x = padding + (i / (values.length - 1)) * (width - padding * 2);
    const y = padding + (1 - (v - min) / (max - min || 1)) * (height - padding * 2);
    return `${x},${y}`;
  });

  const isDown = values[values.length - 1]! <= values[0]!;
  const lineColor = isDown ? 'var(--acc-color-success)' : 'var(--acc-color-danger)';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', overflow: 'visible' }}
      aria-hidden="true"
    >
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke={lineColor}
        strokeWidth={2}
        strokeLinejoin="round"
      />
      {/* Dots */}
      {points.map((pt, i) => {
        const [x, y] = pt.split(',').map(Number);
        return (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={3}
            fill={lineColor}
            opacity={0.7}
          />
        );
      })}
    </svg>
  );
}

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

const styles = {
  page: { minHeight: '100vh', background: 'var(--acc-color-page)' },
  content: { maxWidth: 1080, margin: '0 auto', padding: '36px 28px 40px' },
  breadcrumb: { fontSize: 13, color: 'var(--acc-color-text-muted)', marginBottom: 16 },
  breadLink: { color: 'var(--acc-color-primary)', textDecoration: 'none' },
  h1: { margin: 0, fontSize: 30, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.04em', color: 'var(--acc-color-text)' },
  subtitle: { color: 'var(--acc-color-text-muted)', fontSize: 15, lineHeight: 1.7 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 16,
    margin: '24px 0',
  },
  statCard: {
    background: 'var(--card-bg)',
    border: '1px solid var(--acc-color-border)',
    borderRadius: 20,
    padding: '16px 20px',
    boxShadow: 'var(--acc-shadow-sm)',
  },
  statValue: { fontSize: 36, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.04em' },
  statLabel: { fontSize: 13, color: 'var(--acc-color-text-muted)', marginTop: 6 },
  card: {
    background: 'var(--card-bg)',
    border: '1px solid var(--acc-color-border)',
    borderRadius: 20,
    padding: 24,
    marginBottom: 20,
    boxShadow: 'var(--acc-shadow-sm)',
  },
  cardTitle: { fontWeight: 700, fontSize: 18, marginBottom: 16, color: 'var(--acc-color-text)' },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 13 },
  th: {
    textAlign: 'left' as const,
    padding: '8px 12px',
    borderBottom: '2px solid var(--acc-color-border)',
    color: 'var(--acc-color-text-muted)',
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase' as const,
  },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--acc-color-border)' },
  scanLink: { color: 'var(--acc-color-primary)', textDecoration: 'none', fontWeight: 700 },
  trendRow: { display: 'flex', gap: 32, alignItems: 'flex-start', flexWrap: 'wrap' as const },
  changeUp: { color: 'var(--acc-color-danger)', fontWeight: 700 },
  changeDown: { color: 'var(--acc-color-success)', fontWeight: 700 },
  changeNeutral: { color: 'var(--acc-color-text-muted)' },
  error: { color: 'var(--acc-color-danger)' },
  noData: { color: 'var(--acc-color-text-soft)', fontSize: 14, padding: '20px 0' },
  impactRow: { display: 'flex', gap: 16, flexWrap: 'wrap' as const, marginTop: 12 },
  impactPill: (impact: string) => {
    const cols: Record<string, { bg: string; color: string }> = {
      critical: { bg: 'var(--acc-badge-danger-bg)', color: 'var(--acc-badge-danger-text)' },
      serious:  { bg: 'var(--acc-badge-warning-bg)', color: 'var(--acc-badge-warning-text)' },
      moderate: { bg: 'var(--acc-badge-info-bg)', color: 'var(--acc-badge-info-text)' },
      minor:    { bg: 'var(--acc-color-primary-soft)', color: 'var(--acc-color-primary)' },
    };
    const c = cols[impact] ?? { bg: 'var(--surface-secondary)', color: 'var(--acc-color-text)' };
    return {
      background: c.bg,
      color: c.color,
      borderRadius: 20,
      padding: '6px 14px',
      fontSize: 14,
      fontWeight: 700,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
    };
  },
};

function trendDelta(current: number, previous: number): React.ReactElement | null {
  if (previous === 0) return null;
  const delta = current - previous;
  if (delta === 0) return <span style={styles.changeNeutral}>→ {i18n.t('reports:labels.same')}</span>;
  const arrow = delta > 0 ? '↑' : '↓';
  const className = delta > 0 ? styles.changeUp : styles.changeDown;
  return <span style={className}>{arrow} {Math.abs(delta)}</span>;
}

function formatAuthMode(mode: string): string {
  switch (mode) {
    case 'bearer_token': return i18n.t('reports:labels.authModes.bearerToken');
    case 'session_storage_state': return i18n.t('reports:labels.authModes.sessionStorageState');
    case 'form_login': return i18n.t('reports:labels.authModes.formLogin');
    default: return i18n.t('reports:labels.authModes.publicMode');
  }
}

function formatAuthFailureCategory(category: string | null): string | null {
  if (!category) return null;
  switch (category) {
    case 'session_expired': return i18n.t('reports:labels.authFailures.sessionExpired');
    case 'redirected_to_login_mid_scan': return i18n.t('reports:labels.authFailures.redirectedToLoginMidScan');
    case 'auth_session_invalidated': return i18n.t('reports:labels.authFailures.authSessionInvalidated');
    case 'redirected_to_login': return i18n.t('reports:labels.authFailures.redirectedToLogin');
    case 'selector_missing': return i18n.t('reports:labels.authFailures.selectorMissing');
    case 'success_selector_not_found': return i18n.t('reports:labels.authFailures.successSelectorNotFound');
    case 'success_url_pattern_not_matched': return i18n.t('reports:labels.authFailures.successUrlPatternNotMatched');
    case 'selector_not_found': return i18n.t('reports:labels.authFailures.selectorNotFound');
    case 'auth_token_rejected': return i18n.t('reports:labels.authFailures.authTokenRejected');
    default: return category.replace(/_/g, ' ');
  }
}

function formatScanStatus(status: string): string {
  switch (status) {
    case 'completed': return i18n.t('reports:labels.scanStatuses.completed');
    case 'completed_with_failures': return i18n.t('reports:labels.scanStatuses.completedWithFailures');
    case 'running': return i18n.t('reports:labels.scanStatuses.running');
    case 'queued': return i18n.t('reports:labels.scanStatuses.queued');
    case 'failed': return i18n.t('reports:labels.scanStatuses.failed');
    case 'auth_failed': return i18n.t('reports:labels.scanStatuses.authFailed');
    default: return status.replace(/_/g, ' ');
  }
}

function formatImpactLabel(impact: string): string {
  switch (impact) {
    case 'critical': return i18n.t('reports:labels.severity.critical');
    case 'serious': return i18n.t('reports:labels.severity.serious');
    case 'moderate': return i18n.t('reports:labels.severity.moderate');
    case 'minor': return i18n.t('reports:labels.severity.minor');
    default: return impact.replace(/_/g, ' ');
  }
}

function impactVariant(impact: string): BadgeVariant {
  switch (impact) {
    case 'critical': return 'danger';
    case 'serious': return 'warning';
    case 'moderate': return 'warning';
    case 'minor': return 'neutral';
    default: return 'neutral';
  }
}

function riskVariant(state: 'stable' | 'attention' | 'critical'): BadgeVariant {
  switch (state) {
    case 'critical': return 'danger';
    case 'attention': return 'warning';
    case 'stable':
    default: return 'success';
  }
}

export default function ComplianceReportPage() {
  const { t } = useTranslation(['reports', 'common']);
  const { id } = useParams<{ id: string }>();
  const { org, role } = useAuth();
  const [report, setReport] = useState<ComplianceReport | null>(null);
  const [trend, setTrend] = useState<TrendEntry[]>([]);
  const [usageData, setUsageData] = useState<OrgUsageResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportFeedback, setExportFeedback] = useState<string | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      const [complianceRes, trendRes, usageRes] = await Promise.allSettled([
        api.get<ComplianceReport>(`/reports/sites/${id}/compliance`),
        api.get<{ trend: TrendEntry[] }>(`/reports/sites/${id}/trend`),
        api.get<OrgUsageResponse>('/orgs/me/usage'),
      ]);

      if (complianceRes.status === 'fulfilled') setReport(complianceRes.value);
      if (trendRes.status === 'fulfilled') setTrend(trendRes.value.trend ?? []);
      if (usageRes.status === 'fulfilled') setUsageData(usageRes.value);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('feedback.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenEvidence = useCallback(async (evidenceId: string) => {
    setEvidenceError(null);
    try {
      const response = await fetchAuthenticated(`/evidence/${evidenceId}/file`);
      if (!response.ok) {
        const message =
          response.status === 403 ? t('common:errors.forbidden') :
          response.status === 404 ? t('common:errors.notFound') :
          t('feedback.evidenceFailed');
        setEvidenceError(message);
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Failed to open compliance evidence file', err);
      setEvidenceError(t('feedback.evidenceFailed'));
    }
  }, [t]);

  const handleExportCsv = useCallback(async () => {
    if (!id) return;
    setExportError(null);
    setExportFeedback(null);
    setExportingCsv(true);
    try {
      const response = await fetchAuthenticated(`/reports/sites/${id}/compliance/export.csv`);
      if (response.status === 403) {
        setExportError(t('feedback.growthRequired'));
        return;
      }
      if (!response.ok) {
        setExportError(t('feedback.httpFailed', { status: response.status }));
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const disposition = response.headers.get('Content-Disposition');
      const filenameMatch = disposition?.match(/filename="([^"]+)"/);
      link.download = filenameMatch?.[1] ?? `site-compliance-${id}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setExportFeedback(t('feedback.exported'));

      if (org?.slug) {
        try {
          await api.patch(`/orgs/${org.slug}`, {
            settings: {
              onboarding: {
                exportedReportAt: new Date().toISOString(),
              },
            },
          });
        } catch {
          // Export succeeded; onboarding progress can fail silently here.
        }
      }
    } catch (err) {
      console.error('Failed to export compliance CSV', err);
      setExportError(t('feedback.exportFailed'));
    } finally {
      setExportingCsv(false);
    }
  }, [id, org?.slug, t]);

  if (loading) {
    return (
      <Container maxWidth={1120}>
        <p style={{ color: 'var(--acc-color-text-muted)', fontSize: 14, margin: 0 }}>{t('loading')}</p>
      </Container>
    );
  }

  if (error || !report) {
    return (
      <Container maxWidth={1120}>
          <AlertBanner variant="danger">{error ?? t('feedback.loadFailed')}</AlertBanner>
      </Container>
    );
  }

  const latest = report.latestScan;
  const issueCounts = report.openIssues;
  const trendChronological = [...trend].sort(
    (a, b) => new Date(a.completedAt ?? 0).getTime() - new Date(b.completedAt ?? 0).getTime()
  );
  const canViewInternalReview = role === 'owner' || role === 'admin';
  const historyDepth = usageData?.limits.regressionHistoryDepth ?? trendChronological.length;
  const historyUpgradePlan = historyDepth <= 2 ? 'starter' : 'growth';
  const historyUpgradePath = buildBillingUpgradePath(historyUpgradePlan, 'regression-history-lock');
  const showHistoryUpgrade = usageData !== null && report.totalScans > trendChronological.length;
  const workflowLabel = (state: string) => {
    switch (state) {
      case 'acknowledged': return t('labels.statusAcknowledged');
      case 'inProgress':
      case 'in_progress':
        return t('labels.statusInProgress');
      case 'resolved': return t('labels.statusResolved');
      case 'falsePositive':
      case 'false_positive':
        return t('labels.statusFalsePositive');
      case 'suppressed': return t('labels.statusSuppressed');
      case 'open':
      default:
        return t('labels.statusOpen');
    }
  };
  const riskStateLabel = (state: 'stable' | 'attention' | 'critical') => {
    switch (state) {
      case 'attention': return t('labels.riskStateAttention');
      case 'critical': return t('labels.riskStateCritical');
      case 'stable':
      default:
        return t('labels.riskStateStable');
    }
  };
  const riskSummaryLabel = () => {
    if (report.latestAuthenticatedScan?.status === 'auth_failed') {
      return t('common:riskSummary.authAttention');
    }
    if (report.openIssues.critical > 0) {
      return t('common:riskSummary.criticalBarriers', { count: report.openIssues.critical });
    }
    if (report.openIssues.serious > 0 || report.openIssues.moderate > 0 || report.openIssues.minor > 0) {
      return t('common:riskSummary.remediationActive');
    }
    return t('common:riskSummary.noFindings');
  };
  const regressionStateLabel = (state: 'baseline' | 'improved' | 'stable' | 'regressed') => {
    switch (state) {
      case 'baseline': return t('labels.regressionBaseline');
      case 'improved': return t('labels.regressionImproved');
      case 'regressed': return t('labels.regressionRegressed');
      case 'stable':
      default:
        return t('labels.regressionStable');
    }
  };
  const regressionSummaryLabel = (state: 'baseline' | 'improved' | 'stable' | 'regressed') => {
    switch (state) {
      case 'baseline':
        return t('common:regressionSummary.baseline');
      case 'improved':
        return t('common:regressionSummary.improved');
      case 'regressed':
        return t('common:regressionSummary.regressed');
      case 'stable':
      default:
        return t('common:regressionSummary.stable');
    }
  };
  const reportReview = latest
    ? deriveScanReview({
        status: report.latestAuthenticatedScan?.status === 'auth_failed' ? 'auth_failed' : 'completed',
        pagesDiscovered: null,
        pagesScanned: latest.pagesScanned,
        pagesFailed: null,
        totalIssues: latest.totalIssues,
        severityCounts: {
          critical: report.openIssues.critical,
          serious: report.openIssues.serious,
          moderate: report.openIssues.moderate,
          minor: report.openIssues.minor,
        },
        auth: report.latestAuthenticatedScan
          ? {
              mode: report.latestAuthenticatedScan.mode as 'public' | 'bearer_token' | 'session_storage_state' | 'form_login',
              blockedByAuthFailure: report.latestAuthenticatedScan.status === 'auth_failed',
              failureCategory: report.latestAuthenticatedScan.failureCategory,
              authenticatedPagesScanned: report.latestAuthenticatedScan.authenticatedPagesScanned,
              discoveredRouteCount: report.latestAuthenticatedScan.discoveredRouteCount,
            }
          : null,
        trend: trendChronological,
        issueTypes: report.latestTopIssues.map((issue) => ({
          impact: issue.impact,
          occurrenceCount: issue.occurrenceCount,
          samplePageUrl: issue.samplePageUrl,
          samplePageEvidenceId: issue.samplePageEvidenceId,
          likelyCause: issue.likelyCause,
          developerFixGuidance: issue.developerFixGuidance,
        })),
      })
    : null;

  return (
    <Container maxWidth={1180}>
        {/* Breadcrumb */}
        <div style={styles.breadcrumb}>
          <Link to="/sites" style={styles.breadLink}>{t('common:navigation.sites')}</Link>
          {' › '}
          <Link to={`/sites/${id}`} style={styles.breadLink}>{report.site.name}</Link>
          {' › '}
          {t('header.title')}
        </div>

        <div style={{ marginBottom: 24 }}>
          <PageHeader
            eyebrow={t('header.eyebrow')}
            title={report.site.name}
            subtitle={report.site.baseUrl}
            description={t('header.description')}
            meta={
              <>
                <Badge variant={riskVariant(report.riskSnapshot.state)}>
                  {riskStateLabel(report.riskSnapshot.state)}
                </Badge>
                {report.latestAuthenticatedScan && (
                  <Badge variant={report.latestAuthenticatedScan.status === 'auth_failed' ? 'danger' : 'info'}>
                    {formatAuthMode(report.latestAuthenticatedScan.mode)}
                  </Badge>
                )}
                <Badge variant="neutral">
                  {t('labels.reportRecorded', { count: report.totalScans })}
                </Badge>
              </>
            }
            action={
              <>
                <Link
                  to={`/sites/${report.site.id}`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 40,
                    padding: '9px 16px',
                    borderRadius: 10,
                    border: '1px solid var(--acc-color-border)',
                    background: 'var(--button-secondary-bg)',
                    color: 'var(--acc-color-text)',
                    textDecoration: 'none',
                    fontSize: 14,
                    fontWeight: 600,
                  }}
                >
                  {t('actions.openSiteWorkspace')}
                </Link>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => void handleExportCsv()}
                  disabled={exportingCsv || usageData?.limits.complianceExportEnabled === false}
                >
                  {exportingCsv ? t('reportPack.exporting') : usageData?.limits.complianceExportEnabled === false ? t('reportPack.locked') : t('reportPack.download')}
                </Button>
              </>
            }
          />
        </div>

        <Card style={{ marginBottom: 18, border: '1px solid var(--acc-color-border)', background: 'var(--card-bg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ display: 'grid', gap: 12 }}>
              <BrandLogo variant="navbar" />
              <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc-color-primary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                {t('reportPack.title')}
              </div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--acc-color-text)', marginBottom: 6 }}>
                {t('reportPack.shareReady')}
              </div>
              <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)', lineHeight: 1.7, maxWidth: 720 }}>
                {t('reportPack.summary')}
              </div>
            </div>
            </div>
            <div style={{ display: 'grid', gap: 8, minWidth: 220 }}>
              <div style={{ border: '1px solid var(--acc-color-border)', borderRadius: 10, background: 'var(--surface-secondary)', padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc-color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  {t('reportPack.workspace')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--acc-color-text)' }}>
                  {org?.name ?? t('reportPack.currentWorkspace')}
                </div>
              </div>
              <div style={{ border: '1px solid var(--acc-color-border)', borderRadius: 10, background: 'var(--surface-secondary)', padding: '10px 12px' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--acc-color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                  {t('reportPack.generated')}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--acc-color-text)' }}>
                  {formatDateTime(new Date())}
                </div>
              </div>
            </div>
          </div>
          {usageData?.limits.complianceExportEnabled === false && (
            <div style={{ marginTop: 16, fontSize: 13, color: 'var(--acc-color-text-muted)', lineHeight: 1.6 }}>
              {t('reportPack.lockedHelper')}
            </div>
          )}
        </Card>

        {canViewInternalReview && reportReview && (
          <div style={{ marginBottom: 18 }}>
            <ScanReviewPanel
              review={reportReview}
              compact
              dataTestId="compliance-review-panel"
            />
          </div>
        )}

        {evidenceError && (
          <AlertBanner variant="danger" style={{ marginBottom: 16 }}>
            {evidenceError}
          </AlertBanner>
        )}
        {exportError && (
          <AlertBanner variant="danger" style={{ marginBottom: 16 }}>
            {exportError}
          </AlertBanner>
        )}
        {exportFeedback && (
          <AlertBanner variant="success" style={{ marginBottom: 16 }}>
            {exportFeedback}
          </AlertBanner>
        )}

        {/* Top stats */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 14,
            marginBottom: 24,
          }}
        >
          <StatCard value={report.totalScans} label={t('stats.totalScans')} />
          <StatCard
            value={report.openIssues.critical + report.openIssues.serious + report.openIssues.moderate + report.openIssues.minor}
            label={t('stats.openIssuesLatest')}
            accent={(report.openIssues.critical + report.openIssues.serious + report.openIssues.moderate + report.openIssues.minor) > 0 ? '#d97706' : undefined}
          />
          {latest && (
            <StatCard value={latest.pagesScanned} label={t('stats.pagesLatest')} />
          )}
          {report.wcagCoverage && report.wcagCoverage !== 'none' && (
            <StatCard value={report.wcagCoverage} label={t('stats.wcagLevel')} />
          )}
        </div>

        <div style={styles.grid}>
          <Card>
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={styles.cardTitle}>{t('sections.riskSnapshot')}</div>
              <div>
                <Badge variant={riskVariant(report.riskSnapshot.state)}>
                  {riskStateLabel(report.riskSnapshot.state)}
                </Badge>
              </div>
              <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)', lineHeight: 1.7 }}>
                {riskSummaryLabel()}
              </div>
            </div>
          </Card>
          <Card>
            <div style={styles.cardTitle}>{t('sections.workflowSummary')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, fontSize: 13 }}>
              <div><strong>{report.workflowSummary.open}</strong><div style={styles.statLabel}>{workflowLabel('open')}</div></div>
              <div><strong>{report.workflowSummary.acknowledged}</strong><div style={styles.statLabel}>{workflowLabel('acknowledged')}</div></div>
              <div><strong>{report.workflowSummary.inProgress}</strong><div style={styles.statLabel}>{workflowLabel('inProgress')}</div></div>
              <div><strong>{report.workflowSummary.resolved}</strong><div style={styles.statLabel}>{workflowLabel('resolved')}</div></div>
              <div><strong>{report.workflowSummary.falsePositive}</strong><div style={styles.statLabel}>{workflowLabel('falsePositive')}</div></div>
              <div><strong>{report.suppressedSummary.total}</strong><div style={styles.statLabel}>{workflowLabel('suppressed')}</div></div>
            </div>
          </Card>
          <Card>
            <div style={styles.cardTitle}>{t('sections.authenticatedMonitoring')}</div>
            {report.latestAuthenticatedScan ? (
              <div style={{ display: 'grid', gap: 8, fontSize: 13, color: 'var(--acc-color-text-muted)' }}>
                <div>
                  <strong>{t('labels.authenticatedStatus', {
                    status: formatScanStatus(report.latestAuthenticatedScan.status),
                    mode: formatAuthMode(report.latestAuthenticatedScan.mode),
                  })}</strong>
                </div>
                <div>
                  {t('labels.coverage', {
                    pages: report.latestAuthenticatedScan.authenticatedPagesScanned,
                    routes: report.latestAuthenticatedScan.discoveredRouteCount,
                  })}
                </div>
                {report.latestAuthenticatedScan.failureCategory && (
                  <div>
                    {formatAuthFailureCategory(report.latestAuthenticatedScan.failureCategory)}
                  </div>
                )}
                <div>
                  <Link to={`/scans/${report.latestAuthenticatedScan.scanId}`} style={styles.scanLink}>{t('actions.openAuthScan')}</Link>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)' }}>{t('labels.noAuthScan')}</div>
            )}
          </Card>
          <Card>
            <div style={styles.cardTitle}>{t('sections.latestRegression')}</div>
            {report.latestRegression ? (
              <div style={{ display: 'grid', gap: 8, fontSize: 13, color: 'var(--acc-color-text-muted)' }}>
                <div><strong>{regressionStateLabel(report.latestRegression.state)}</strong></div>
                <div>{regressionSummaryLabel(report.latestRegression.state)}</div>
                <div>
                  {t('labels.scoreDelta', { value: `${report.latestRegression.scoreDelta > 0 ? '+' : ''}${report.latestRegression.scoreDelta}` })}
                  {' · '}
                  {t('labels.issuesDelta', { value: `${report.latestRegression.issuesDelta > 0 ? '+' : ''}${report.latestRegression.issuesDelta}` })}
                  {' · '}
                  {t('labels.criticalDelta', { value: `${report.latestRegression.criticalDelta > 0 ? '+' : ''}${report.latestRegression.criticalDelta}` })}
                </div>
                <div>{t('labels.reappeared', { count: report.latestRegression.reappearedIssueCount })}</div>
                <div>
                  <Link to={`/scans/${report.latestRegression.scanId}`} style={styles.scanLink}>{t('actions.openComparedScan')}</Link>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--acc-color-text-muted)' }}>{t('labels.noRegression')}</div>
            )}
          </Card>
        </div>

        {/* Latest scan breakdown */}
        {latest && (
          <Card style={{ marginBottom: 20 }}>
            <SectionHeader
              title={t('sections.latestIssueMix')}
              subtitle={t('sections.latestIssueMixSubtitle')}
              action={<Link to={`/scans/${latest.id}`} style={styles.scanLink}>{t('actions.viewFullReport')}</Link>}
            />
            <div style={styles.impactRow}>
              {(['critical', 'serious', 'moderate', 'minor'] as const).map((impact) => (
                <div
                  key={impact}
                  style={{
                    border: '1px solid var(--acc-color-border)',
                    borderRadius: 12,
                    background: 'var(--surface-secondary)',
                    padding: '12px 16px',
                    minWidth: 112,
                  }}
                >
                  <div style={{ fontSize: 26, fontWeight: 800, color: impact === 'critical' ? 'var(--acc-color-danger)' : impact === 'serious' ? 'var(--acc-color-warning)' : 'var(--acc-color-text)', lineHeight: 1 }}>
                    {issueCounts[impact]}
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <Badge variant={impactVariant(impact)}>{formatImpactLabel(impact)}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {latest && report.latestTopIssues.length > 0 && (
          <Card style={{ marginBottom: 20 }}>
            <SectionHeader
              title={t('sections.latestRemediation')}
              subtitle={t('sections.latestRemediationSubtitle')}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {report.latestTopIssues.map((issue) => (
                <div
                  key={issue.issueId}
                  style={{
                    border: '1px solid var(--acc-color-border)',
                    borderRadius: 12,
                    padding: '16px 18px',
                    background: 'var(--surface-secondary)',
                    display: 'grid',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--acc-color-text)', marginBottom: 6, fontSize: 15 }}>{issue.title}</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Badge variant={impactVariant(issue.impact)}>{formatImpactLabel(issue.impact)}</Badge>
                        <Badge variant="info">WCAG {issue.wcagCriteria}</Badge>
                        <span style={{ color: 'var(--acc-color-text-muted)', fontSize: 12, alignSelf: 'center' }}>
                          {t('labels.occurrences', { count: issue.occurrenceCount })}
                        </span>
                      </div>
                    </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        {issue.samplePageEvidenceId && (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => void handleOpenEvidence(issue.samplePageEvidenceId!)}
                          >
                            {t('actions.openEvidence')}
                          </Button>
                        )}
                        <Link to={issue.deepLinks.scanDetailPath} style={styles.scanLink}>{t('actions.openScan')}</Link>
                        <Link to={issue.deepLinks.siteCompliancePath} style={styles.scanLink}>{t('actions.openWorkflow')}</Link>
                    </div>
                  </div>

                  <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.6 }}>
                    {issue.samplePageUrl
                      ? t('labels.affectedRoute', { route: issue.samplePageUrl })
                      : t('common:review.honesty.noSampleRoute')}
                  </div>

                  {!issue.samplePageEvidenceId && (
                    <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', lineHeight: 1.6 }}>
                      {t('common:review.honesty.noEvidence')}
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <Badge variant="info">{workflowLabel(issue.workflowState)}</Badge>
                    {issue.suppressedCount > 0 && (
                      <Badge variant="neutral">{t('labels.suppressed', { count: issue.suppressedCount })}</Badge>
                    )}
                  </div>

                  <div style={{ fontSize: 13, color: 'var(--acc-color-text)', lineHeight: 1.6 }}>
                    <strong>{t('labels.likelyCause')}</strong>{' '}
                    {issue.likelyCause?.trim() || t('common:review.honesty.noLikelyCause')}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--acc-color-text)', lineHeight: 1.6 }}>
                    <strong>{t('labels.developerGuidance')}</strong>{' '}
                    {issue.developerFixGuidance?.trim() || t('common:review.honesty.noDeveloperGuidance')}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Trend chart */}
        {trendChronological.length >= 2 && (
          <Card style={{ marginBottom: 20 }}>
            <SectionHeader
              title={t('sections.trend')}
              subtitle={t('sections.trendSubtitle')}
            />
            <div style={styles.trendRow}>
              <div>
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', marginBottom: 8 }}>{t('labels.totalIssues')}</div>
                <TrendSparkline data={trendChronological} valueKey="totalIssues" />
              </div>
              <div>
                <div style={{ fontSize: 12, color: 'var(--acc-color-text-muted)', marginBottom: 8 }}>{t('labels.criticalIssues')}</div>
                <TrendSparkline data={trendChronological} valueKey="criticalIssues" />
              </div>
            </div>
          </Card>
        )}

        {/* Trend table */}
        {trendChronological.length > 0 && (
          <Card style={{ marginBottom: 20 }}>
            <SectionHeader
              title={t('sections.scanHistory')}
              subtitle={t('sections.scanHistorySubtitle')}
            />
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>{t('labels.date')}</th>
                  <th style={styles.th}>{t('labels.pages')}</th>
                  <th style={styles.th}>{t('labels.totalIssuesShort')}</th>
                  <th style={styles.th}>{t('labels.change')}</th>
                  <th style={styles.th}>{t('labels.critical')}</th>
                  <th style={styles.th}></th>
                </tr>
              </thead>
              <tbody>
                {[...trendChronological].reverse().map((entry, idx, arr) => {
                  const prev = arr[idx + 1];
                  return (
                    <tr key={entry.scanId}>
                      <td style={styles.td}>{formatDate(entry.completedAt) || '—'}</td>
                      <td style={styles.td}>{entry.pagesScanned}</td>
                      <td style={styles.td}>{entry.totalIssues}</td>
                      <td style={styles.td}>
                        {prev ? trendDelta(entry.totalIssues, prev.totalIssues) : <span style={styles.changeNeutral}>—</span>}
                      </td>
                      <td style={styles.td}>
                        {entry.criticalIssues > 0
                          ? <span style={{ color: 'var(--acc-color-danger)', fontWeight: 700 }}>{entry.criticalIssues}</span>
                          : entry.criticalIssues}
                      </td>
                      <td style={styles.td}>
                        <Link to={`/scans/${entry.scanId}`} style={styles.scanLink}>{t('actions.viewScan')}</Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}

        {showHistoryUpgrade && (
          <div style={{ marginBottom: 20 }}>
            <LockedFeatureState
              tierLabel={t('common:plans.planTier', { plan: getPlanLabel(usageData?.plan) })}
              title={t('historyLock.title')}
              description={t('historyLock.description', { count: historyDepth })}
              benefits={[
                {
                  title: t('historyLock.benefits.compareMore.title'),
                  body: t('historyLock.benefits.compareMore.body'),
                },
                {
                  title: t('historyLock.benefits.preserveHistory.title'),
                  body: t('historyLock.benefits.preserveHistory.body'),
                },
                {
                  title: t('historyLock.benefits.auditReviews.title'),
                  body: t('historyLock.benefits.auditReviews.body'),
                },
              ]}
              upgradeLabel={t('historyLock.upgradeLabel', { plan: getPlanLabel(historyUpgradePlan) })}
              onUpgrade={() => { window.location.href = historyUpgradePath; }}
              onPricing={() => { window.location.href = '/pricing'; }}
            />
          </div>
        )}

        {trendChronological.length === 0 && (
          <p style={styles.noData}>{t('labels.noTrend')}</p>
        )}
    </Container>
  );
}
